import React from "react";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment";
import { decrypt } from "../../utils";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fbfaf8",
    paddingTop: 20,
    paddingBottom: 18,
    paddingHorizontal: 22,
    fontSize: 9,
    color: "#1f2937",
    fontFamily: "Helvetica",
  },
  topCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1 solid #e9e3da",
    padding: 11,
    marginBottom: 8,
  },
  topHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 9,
    borderBottom: "1 solid #efe9df",
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#1f2937",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  avatarImage: {
    width: 42,
    height: 42,
    borderRadius: 21,
    marginRight: 12,
    objectFit: "cover",
  },
  avatarText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
  },
  titleBlock: {
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 5,
  },
  name: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1f2937",
  },
  headerMeta: {
    fontSize: 7,
    color: "#c0b6a8",
    textAlign: "right",
    maxWidth: 210,
  },
  summaryRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  summaryItem: {
    fontSize: 7,
    color: "#7b8190",
    marginRight: 12,
    marginBottom: 2,
  },
  metricRow: {
    flexDirection: "row",
    paddingTop: 9,
  },
  metricCell: {
    flex: 1,
    paddingHorizontal: 8,
    borderRight: "1 solid #efe9df",
  },
  metricCellFirst: {
    paddingLeft: 0,
  },
  metricCellLast: {
    borderRight: "0 solid transparent",
    paddingRight: 0,
  },
  metricLabel: {
    fontSize: 6.5,
    textTransform: "uppercase",
    color: "#9b9fa8",
    marginBottom: 4,
    fontWeight: 700,
  },
  metricValue: {
    fontSize: 11,
    color: "#2d3138",
    fontWeight: 700,
  },
  contentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  leftCol: {
    width: "50%",
    paddingRight: 5,
  },
  rightCol: {
    width: "50%",
    paddingLeft: 5,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    border: "1 solid #e9e3da",
    overflow: "hidden",
    marginBottom: 8,
  },
  cardHeader: {
    backgroundColor: "#f3eee6",
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardHeaderText: {
    fontSize: 9,
    fontWeight: 700,
    color: "#383d45",
    textTransform: "uppercase",
  },
  cardBody: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  infoRow: {
    flexDirection: "row",
    paddingVertical: 5,
    borderBottom: "1 solid #f2ede5",
  },
  infoLabel: {
    width: "42%",
    color: "#8a8680",
    fontSize: 8,
  },
  infoValue: {
    width: "58%",
    color: "#202124",
    fontSize: 8.5,
    fontWeight: 600,
    lineHeight: 1.25,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 6,
  },
  chip: {
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 6,
    marginBottom: 4,
    backgroundColor: "#fff6dc",
    border: "1 solid #f0b437",
  },
  chipText: {
    fontSize: 7,
    color: "#8c6208",
    fontWeight: 700,
  },
  noteBox: {
    backgroundColor: "#f6f1ea",
    borderRadius: 7,
    padding: 8,
    marginBottom: 6,
  },
  noteText: {
    fontSize: 8,
    lineHeight: 1.3,
    color: "#626976",
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "50%",
    paddingRight: 10,
    marginBottom: 5,
  },
  listDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#e2b33f",
    marginTop: 5,
    marginRight: 8,
  },
  listText: {
    flex: 1,
    fontSize: 8,
    color: "#535a66",
    lineHeight: 1.25,
  },
  listGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  footer: {
    position: "absolute",
    left: 22,
    right: 22,
    bottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 6,
    color: "#c0b6a8",
  },
});

const formatDate = (value) => (value ? moment(value).format("MM/DD/YYYY") : "—");
const normalizePhone = (value) => value || "—";
const joinAddress = (...parts) => parts.filter(Boolean).join(", ");

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "CL";

const RowList = ({ rows }) =>
  rows.map((row, index) => (
    <View
      key={row.label}
      style={[
        styles.infoRow,
        index === rows.length - 1 ? { borderBottom: "0 solid transparent" } : null,
      ]}
    >
      <Text style={styles.infoLabel}>{row.label}</Text>
      <Text style={styles.infoValue}>{row.value || "—"}</Text>
    </View>
  ));

const EMSFormPDF = ({ clientData, profile, formData }) => {
  const companyName =
    profile?.business?.companyName ||
    clientData?.business?.companyName ||
    clientData?.companyName ||
    profile?.facility?.name ||
    "Business";
  const facilityName = profile?.facility?.name || companyName;
  const facilityAddress =
    joinAddress(
      profile?.facility?.address,
      profile?.facility?.city,
      profile?.facility?.state,
      profile?.facility?.zipCode
    ) ||
    joinAddress(
      clientData?.facility?.address,
      clientData?.facility?.city,
      clientData?.facility?.state,
      clientData?.facility?.zipCode
    ) ||
    clientData?.form1?.previousAddress ||
    "—";
  const facilityRows = [
    { label: "Facility transferred from", value: facilityName },
    {
      label: "Facility phone",
      value: profile?.facility?.phoneNumber || profile?.business?.phoneNumber || "—",
    },
    { label: "Address", value: facilityAddress },
    {
      label: "Contact name",
      value: clientData?.form2?.caseManagerName || profile?.user?.name || "—",
    },
    {
      label: "Cell phone",
      value: normalizePhone(clientData?.form2?.caseManagerPhoneNumber),
    },
    {
      label: "Email",
      value: clientData?.form2?.caseManagerEmail || profile?.user?.email || "—",
    },
  ];
  const medicationRows = [
    { label: "Current pharmacy", value: clientData?.form2?.pharmacyName || "—" },
    {
      label: "Pharmacy phone",
      value: normalizePhone(clientData?.form2?.pharmacyPhoneNumber),
    },
    {
      label: "Pharmacy address",
      value: clientData?.form2?.pharmacyAddress || clientData?.form2?.pharmacyEmail || "—",
    },
  ];
  const contactRows = [
    { label: "Primary care physician", value: clientData?.form2?.pcpName || "—" },
    {
      label: "Physician phone",
      value: clientData?.form2?.pcpPhoneNumber || clientData?.form2?.pcpEmail || "—",
    },
    {
      label: "Authorized rep / POA",
      value: clientData?.form2?.emergencyContactName || "—",
    },
    {
      label: "Rep phone",
      value: normalizePhone(clientData?.form2?.emergencyContactPhoneNumber),
    },
  ];
  const conditionChecks = Object.entries(formData?.conditionSelections || {})
    .filter(([, checked]) => checked)
    .map(([label]) => label);
  const alertStatus = Object.entries(formData?.alertSelections || {})
    .filter(([, checked]) => checked)
    .map(([label]) => label);
  const allergies = (clientData?.form1?.knownAllergies || []).filter(Boolean);
  const rawImageSrc = clientData?.imageurl ? decrypt(clientData?.imageurl) : null;
  const imageSrc = rawImageSrc
    ? `${rawImageSrc}${rawImageSrc.includes("?") ? "&" : "?"}pdf=1`
    : null;

  return (
    <Document title={`${clientData?.name || "Client"} EMS Form`}>
      <Page size="A4" style={styles.page} wrap={false}>
        <View style={styles.topCard}>
          <View style={styles.topHeader}>
            {imageSrc ? (
              <Image src={imageSrc} style={styles.avatarImage} />
            ) : (
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{getInitials(clientData?.name)}</Text>
              </View>
            )}
            <View style={styles.titleBlock}>
              <View style={styles.titleRow}>
                <Text style={styles.name}>{clientData?.name || "Client"}</Text>
                <Text style={styles.headerMeta}>
                  {`${companyName} · EMS Form · ${moment().format("MM/DD/YYYY")}`}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryItem}>{clientData?.form1?.gender || "—"}</Text>
                <Text style={styles.summaryItem}>{`DOB: ${formatDate(clientData?.form1?.dob)}`}</Text>
                <Text style={styles.summaryItem}>{`Admitted: ${formatDate(clientData?.form1?.admissionDate)}`}</Text>
                <Text style={styles.summaryItem}>{facilityName}</Text>
              </View>
            </View>
          </View>

          <View style={styles.metricRow}>
            {[
              { label: "Resident", value: clientData?.name || "—" },
              { label: "Date of birth", value: formatDate(clientData?.form1?.dob) },
              { label: "Facility transferred from", value: facilityName },
              { label: "Physician", value: clientData?.form2?.pcpName || "—" },
              { label: "Allergies", value: allergies.join(", ") || "None" },
            ].map((item, index) => (
              <View
                key={item.label}
                style={[
                  styles.metricCell,
                  index === 0 ? styles.metricCellFirst : null,
                  index === 4 ? styles.metricCellLast : null,
                ]}
              >
                <Text style={styles.metricLabel}>{item.label}</Text>
                <Text style={styles.metricValue}>{item.value}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.contentRow}>
          <View style={styles.leftCol}>
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderText}>Facility Information</Text>
              </View>
              <View style={styles.cardBody}>
                <RowList rows={facilityRows} />
              </View>
            </View>

            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderText}>Medical Contacts, DNR & HIPAA</Text>
              </View>
              <View style={styles.cardBody}>
                <RowList rows={contactRows} />
                {[
                  {
                    label: "Advanced Directives / DNR / POLST attached",
                    checked: formData?.attachmentChecks?.advancedDirectives,
                  },
                  {
                    label: "Face Sheet attached",
                    checked: formData?.attachmentChecks?.faceSheet,
                  },
                  {
                    label: "HIPAA Release attached",
                    checked: formData?.attachmentChecks?.hipaaRelease,
                  },
                ]
                  .filter((item) => item.checked)
                  .map((item) => (
                  <View key={item.label} style={styles.chip}>
                    <Text style={styles.chipText}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.rightCol}>
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderText}>Medication Information</Text>
              </View>
              <View style={styles.cardBody}>
                <View style={styles.chipRow}>
                  <View style={styles.chip}>
                    <Text style={styles.chipText}>
                      {`Medication services: ${(clientData?.form3 || []).length > 0 ? "Yes" : "No"}`}
                    </Text>
                  </View>
                </View>
                <RowList rows={medicationRows} />
              </View>
            </View>

            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardHeaderText}>Physical & Mental Conditions</Text>
              </View>
              <View style={styles.cardBody}>
              <View style={styles.noteBox}>
                  <Text style={styles.noteText}>
                    {conditionChecks.length
                      ? conditionChecks.join(" · ")
                      : "No physical or mental conditions were selected."}
                  </Text>
                </View>
                {alertStatus.length ? (
                  <View style={styles.chipRow}>
                    {alertStatus.map((item) => (
                      <View key={item} style={styles.chip}>
                        <Text style={styles.chipText}>{item}</Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.noteBox}>
                    <Text style={styles.noteText}>No alert and orientation items were selected.</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderText}>Complete at Time of Emergency</Text>
          </View>
          <View style={styles.cardBody}>
            <View style={styles.noteBox}>
              <Text style={styles.noteText}>
                {formData?.emergencyNotes || "No emergency notes entered."}
              </Text>
            </View>

              <View style={styles.listGrid}>
              {[
                { label: "Name of staff and date", value: formData?.staffAndDate || "—" },
                {
                  label: "Hospital / destination",
                  value: formData?.didNotGoToHospital
                    ? "Resident did not go to the hospital"
                    : formData?.hospitalDestination || "—",
                },
                {
                  label: "Authorized representative notified",
                  value: formData?.authorizedRepNotified ? "Yes" : "No",
                },
                {
                  label: "Authorized representative / POA",
                  value: formData?.authorizedRepresentative || "—",
                },
                {
                  label: "Contacted by",
                  value: formData?.authorizedRepContactedBy || "—",
                },
              { label: "Notification date", value: formData?.notificationDate || "—" },
            ].map((item) => (
              <View key={item.label} style={styles.listItem}>
                <View style={styles.listDot} />
                <Text style={styles.listText}>{`${item.label}: ${item.value}`}</Text>
              </View>
            ))}
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{`${companyName} · EMS Form`}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Generated by Ogiso EHR · ${moment().format("MM/DD/YYYY")} · Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default EMSFormPDF;
